"use client"

import React from "react"

import { applyPromotions } from "@lib/data/cart"
import { convertToLocale } from "@lib/util/money"
import { HttpTypes } from "@medusajs/types"
import Trash from "@modules/common/icons/trash"
import ErrorMessage from "../error-message"
import { SubmitButton } from "../submit-button"

type DiscountCodeProps = {
  cart: HttpTypes.StoreCart & {
    promotions: HttpTypes.StorePromotion[]
  }
}

const DiscountCode: React.FC<DiscountCodeProps> = ({ cart }) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState("")

  const { promotions = [] } = cart
  const removePromotionCode = async (code: string) => {
    const validPromotions = promotions.filter(
      (promotion) => promotion.code !== code
    )

    const result = await applyPromotions(
      validPromotions.filter((p) => p.code !== undefined).map((p) => p.code!)
    )

    if (!result.success) {
      setErrorMessage(result.error || "Failed to remove promotion")
    }
  }

  const addPromotionCode = async (formData: FormData) => {
    setErrorMessage("")

    const code = formData.get("code")
    if (!code) {
      return
    }
    const input = document.getElementById("promotion-input") as HTMLInputElement
    const codes = promotions
      .filter((p) => p.code !== undefined)
      .map((p) => p.code!)
    codes.push(code.toString())

    const result = await applyPromotions(codes)

    if (!result.success) {
      setErrorMessage(result.error || "Failed to apply promotion")
    }

    if (input) {
      input.value = ""
    }
  }

  return (
    <div className="w-full flex flex-col">
      <div className="text-sm">
        <form action={(a) => addPromotionCode(a)} className="w-full">
          <label className="flex gap-x-1 my-2 items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              type="button"
              className="text-sm text-brand-primary hover:text-brand-primary-dark font-medium"
              data-testid="add-discount-button"
            >
              Add Promotion Code(s)
            </button>
          </label>

          {isOpen && (
            <>
              <div className="flex w-full gap-x-2">
                <input
                  className="w-full h-10 px-4 text-sm bg-white border border-gray-300 rounded-md focus:outline-none focus:border-brand-primary"
                  id="promotion-input"
                  name="code"
                  type="text"
                  autoFocus={false}
                  data-testid="discount-input"
                />
                <SubmitButton
                  variant="secondary"
                  data-testid="discount-apply-button"
                >
                  Apply
                </SubmitButton>
              </div>

              <ErrorMessage
                error={errorMessage}
                data-testid="discount-error-message"
              />
            </>
          )}
        </form>

        {promotions.length > 0 && (
          <div className="w-full flex items-center">
            <div className="flex flex-col w-full">
              <h3 className="text-sm font-medium mb-2">
                Promotion(s) applied:
              </h3>

              {promotions.map((promotion) => {
                return (
                  <div
                    key={promotion.id}
                    className="flex items-center justify-between w-full max-w-full mb-2"
                    data-testid="discount-row"
                  >
                    <span className="flex gap-x-1 items-baseline text-sm w-4/5 pr-1">
                      <span className="truncate" data-testid="discount-code">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${promotion.is_automatic ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}`}>
                          {promotion.code}
                        </span>{" "}
                        (
                        {promotion.application_method?.value !== undefined &&
                          promotion.application_method.currency_code !==
                            undefined && (
                            <>
                              {promotion.application_method.type ===
                              "percentage"
                                ? `${promotion.application_method.value}%`
                                : convertToLocale({
                                    amount: +promotion.application_method.value,
                                    currency_code:
                                      promotion.application_method
                                        .currency_code,
                                  })}
                            </>
                          )}
                        )
                      </span>
                    </span>
                    {!promotion.is_automatic && (
                      <button
                        className="flex items-center text-gray-500 hover:text-brand-accent"
                        onClick={() => {
                          if (!promotion.code) {
                            return
                          }

                          removePromotionCode(promotion.code)
                        }}
                        data-testid="remove-discount-button"
                      >
                        <Trash size={14} />
                        <span className="sr-only">
                          Remove discount code from order
                        </span>
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default DiscountCode
